import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { hexToRgb } from '../utils/color'
import { BeadBoard, isBluetoothSupported } from '../utils/beadBoard'

const AXIS_LABEL_COLOR = '#6b6475'
const AXIS_STRIP_BG = '#f0edf4'

function mixToGray(hex, alpha) {
  // 非聚焦色号降为浅灰（白底下的淡化）
  const { r, g, b } = hexToRgb(hex)
  const gray = 228
  return `rgb(${Math.round(r * alpha + gray * (1 - alpha))}, ${Math.round(g * alpha + gray * (1 - alpha))}, ${Math.round(b * alpha + gray * (1 - alpha))})`
}

export default function BeadPlacingMode({ open, pattern, brand, onClose }) {
  const { isPermanent } = useAuth()
  const [mode, setMode] = useState('all') // all | single | sequence
  const [selectedColorId, setSelectedColorId] = useState(null)
  const [seqIndex, setSeqIndex] = useState(0)
  const [doneSet, setDoneSet] = useState(() => new Set())
  const [mirrored, setMirrored] = useState(false)
  const [zoom, setZoom] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [sizeTick, setSizeTick] = useState(0)
  const [board, setBoard] = useState(null)
  const [boardStatus, setBoardStatus] = useState('')
  const canvasRef = useRef(null)
  const containerRef = useRef(null)
  const dragRef = useRef(null)
  const boardRef = useRef(null)

  const colorMap = useMemo(() => new Map(brand.colors.map((c) => [c.id, c])), [brand])
  // 依次显示：按用量从多到少排序
  const sortedLegend = useMemo(
    () => [...(pattern?.legend || [])].sort((a, b) => b.count - a.count),
    [pattern]
  )

  // 当前聚焦色号
  const activeColorId = useMemo(() => {
    if (mode === 'single') return selectedColorId
    if (mode === 'sequence') return sortedLegend[seqIndex]?.id || null
    return null
  }, [mode, selectedColorId, sortedLegend, seqIndex])

  // 当前色号的全部格子坐标
  const activeCells = useMemo(() => {
    if (!activeColorId || !pattern) return []
    const cells = []
    const { grid, width, height } = pattern
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (grid[y][x] === activeColorId) {
          cells.push({ x: mirrored ? width - 1 - x : x, y })
        }
      }
    }
    return cells
  }, [activeColorId, pattern, mirrored])

  const activeColor = activeColorId ? colorMap.get(activeColorId) : null
  const activeCount = sortedLegend.find((l) => l.id === activeColorId)?.count || 0

  // ===== 渲染 =====
  useEffect(() => {
    if (!open || !pattern) return
    const canvas = canvasRef.current
    if (!canvas) return
    const { grid, width, height } = pattern
    // 测量外层容器（不能测内层——内层尺寸由画布自身决定，会循环塌缩）
    const container = containerRef.current
    if (!container) return
    const availW = container.clientWidth - 16
    const availH = container.clientHeight - 16
    // 额外留白：保证最外圈坐标轴数字完整可见（不贴画布边缘）
    const baseCell = Math.max(3, Math.min(32, Math.floor(Math.min(availW / (width + 3.2), availH / (height + 3.2)))))
    const cell = baseCell * zoom
    const outerPad = cell * 0.6 // 坐标条外侧留白，防止数字贴边被裁
    const W = (width + 2) * cell + outerPad * 2
    const H = (height + 2) * cell + outerPad * 2
    canvas.width = W
    canvas.height = H
    const ctx = canvas.getContext('2d')

    // 白底（拼豆板本色）
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, W, H)

    // 坐标条
    ctx.fillStyle = AXIS_STRIP_BG
    ctx.fillRect(outerPad, outerPad, W - outerPad * 2, cell)
    ctx.fillRect(outerPad, H - outerPad - cell, W - outerPad * 2, cell)
    ctx.fillRect(outerPad, outerPad, cell, H - outerPad * 2)
    ctx.fillRect(W - outerPad - cell, outerPad, cell, H - outerPad * 2)

    // 坐标数字（每格标注；格子过小时每 5 格标注）
    const labelEvery = cell >= 10 ? 1 : 5
    ctx.fillStyle = AXIS_LABEL_COLOR
    ctx.font = `bold ${Math.max(7, Math.floor(cell * 0.4))}px sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    for (let x = 0; x < width; x++) {
      if ((x + 1) % labelEvery !== 0 && x !== 0) continue
      const label = String(x + 1)
      const cx = outerPad + (x + 1) * cell + cell / 2
      ctx.fillText(label, cx, outerPad + cell / 2)
      ctx.fillText(label, cx, H - outerPad - cell / 2)
    }
    for (let y = 0; y < height; y++) {
      if ((y + 1) % labelEvery !== 0 && y !== 0) continue
      const label = String(y + 1)
      const cy = outerPad + (y + 1) * cell + cell / 2
      ctx.fillText(label, outerPad + cell / 2, cy)
      ctx.fillText(label, W - outerPad - cell / 2, cy)
    }

    // 格子
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const id = grid[y][x]
        const color = colorMap.get(id)
        const gx = mirrored ? width - 1 - x : x
        const px = outerPad + (gx + 1) * cell
        const py = outerPad + (y + 1) * cell
        if (!color) {
          ctx.fillStyle = '#f4f2f6'
          ctx.fillRect(px, py, cell, cell)
          continue
        }
        const isActive = activeColorId && id === activeColorId
        if (activeColorId && !isActive) {
          // 非聚焦色号：降为暗灰
          ctx.fillStyle = mixToGray(color.hex, 0.15)
        } else {
          ctx.fillStyle = color.hex
        }
        ctx.fillRect(px, py, cell, cell)
        // 聚焦色号：保持原色显示（不做描边/变暗，其余格子已降为浅灰形成对比）
        // 智能拼豆板同步逻辑不受影响（始终发送原色）
      }
    }

    // 5×5 组线
    if (cell >= 6) {
      ctx.strokeStyle = 'rgba(60, 50, 70, 0.28)'
      ctx.lineWidth = 1
      ctx.beginPath()
      for (let i = 0; i <= width; i += 5) {
        const x = outerPad + (i + 1) * cell + 0.5
        ctx.moveTo(x, outerPad + cell)
        ctx.lineTo(x, H - outerPad - cell)
      }
      for (let i = 0; i <= height; i += 5) {
        const y = outerPad + (i + 1) * cell + 0.5
        ctx.moveTo(outerPad + cell, y)
        ctx.lineTo(W - outerPad - cell, y)
      }
      ctx.stroke()
    }
  }, [open, pattern, colorMap, activeColorId, mirrored, zoom, sizeTick])

  // 进入时重置视图
  useEffect(() => {
    if (!open) return
    setMode('all')
    setSelectedColorId(null)
    setSeqIndex(0)
    setDoneSet(new Set())
    setZoom(1)
    setOffset({ x: 0, y: 0 })
  }, [open, pattern])

  // 退出时断开拼豆板
  useEffect(() => {
    if (open) return
    if (boardRef.current) {
      boardRef.current.disconnect()
      boardRef.current = null
      setBoard(null)
      setBoardStatus('')
    }
  }, [open])

  // 色号变化 → 同步拼豆板
  useEffect(() => {
    if (!board?.connected) return
    if (activeColor && activeCells.length > 0) {
      board.syncColor(activeColor.hex, activeCells)
        .then(({ dropped }) => {
          setBoardStatus(dropped > 0 ? `已同步，${dropped} 格超出板子范围被忽略` : '已同步到拼豆板')
        })
        .catch(() => setBoardStatus('同步失败'))
    } else {
      board.clear().catch(() => {})
      setBoardStatus('已清屏')
    }
  }, [board, activeColor, activeCells])

  // 容器尺寸变化（窗口旋转/面板加载）时重排画布，保证坐标轴完整可见
  useEffect(() => {
    if (!open) return
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setSizeTick((t) => t + 1))
    ro.observe(el)
    return () => ro.disconnect()
  }, [open])

  // 滚轮缩放（桌面端；需非 passive 监听才能 preventDefault）
  useEffect(() => {
    if (!open) return
    const el = containerRef.current
    if (!el) return
    const onWheel = (e) => {
      e.preventDefault()
      setZoom((z) => Math.min(5, Math.max(0.3, z * (e.deltaY < 0 ? 1.12 : 0.89))))
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [open])

  // 单指拖动 + 双指捏合缩放
  const pointersRef = useRef(new Map())
  const pinchRef = useRef(null) // { startDist, startZoom }
  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId)
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()]
      pinchRef.current = { startDist: Math.hypot(a.x - b.x, a.y - b.y), startZoom: zoom }
      dragRef.current = null
    } else {
      dragRef.current = { x: e.clientX - offset.x, y: e.clientY - offset.y }
    }
  }
  const onPointerMove = (e) => {
    if (!pointersRef.current.has(e.pointerId)) return
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pinchRef.current && pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()]
      const dist = Math.hypot(a.x - b.x, a.y - b.y)
      if (pinchRef.current.startDist > 0) {
        setZoom(Math.min(5, Math.max(0.3, pinchRef.current.startZoom * (dist / pinchRef.current.startDist))))
      }
      return
    }
    if (!dragRef.current) return
    setOffset({ x: e.clientX - dragRef.current.x, y: e.clientY - dragRef.current.y })
  }
  const onPointerUp = (e) => {
    pointersRef.current.delete(e.pointerId)
    if (pointersRef.current.size < 2) pinchRef.current = null
    if (pointersRef.current.size === 0) dragRef.current = null
  }

  const handleConnectBoard = async () => {
    if (!isBluetoothSupported()) {
      setBoardStatus('当前浏览器不支持蓝牙连接，请使用 Chrome / Edge 浏览器（iOS 与微信暂不支持）')
      return
    }
    if (board?.connected) {
      board.disconnect()
      boardRef.current = null
      setBoard(null)
      setBoardStatus('已断开')
      return
    }
    setBoardStatus('连接中…')
    try {
      const b = new BeadBoard()
      b.onDisconnect = () => {
        boardRef.current = null
        setBoard(null)
        setBoardStatus('拼豆板已断开')
      }
      await b.connect()
      boardRef.current = b
      setBoard(b)
      setBoardStatus(b.sideLength ? `已连接（${b.sideLength}×${b.sideLength} 板）` : '已连接')
      await b.setBrightness(60).catch(() => {})
    } catch (err) {
      const msg = String(err?.message || err)
      if (msg.includes('User cancelled') || msg.includes('cancelled')) {
        setBoardStatus('')
      } else {
        setBoardStatus(`连接失败：支持 BeadLink 开放协议设备与像素糖果系列拼豆板，更多设备陆续开放中`)
      }
    }
  }

  const toggleDone = () => {
    if (!activeColorId) return
    const next = new Set(doneSet)
    if (next.has(activeColorId)) next.delete(activeColorId)
    else next.add(activeColorId)
    setDoneSet(next)
    // 依次模式：打勾后自动跳到下一个未完成色号
    if (mode === 'sequence' && next.has(activeColorId)) {
      const nextIdx = sortedLegend.findIndex((l, i) => i > seqIndex && !next.has(l.id))
      if (nextIdx !== -1) setSeqIndex(nextIdx)
    }
  }

  if (!open) return null

  // 永久会员专属门禁
  if (!isPermanent) {
    return createPortal(
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
        <div className="w-full max-w-sm rounded-3xl bg-white p-6 text-center shadow-card">
          <p className="mb-2 text-3xl">🔒</p>
          <h3 className="mb-2 text-lg font-bold text-cocoa">拼豆模式</h3>
          <p className="mb-4 text-sm text-cocoa-light">逐色号高亮辅助拼图、智能拼豆板联动，为永久会员专属功能~</p>
          <div className="flex justify-center gap-2">
            <button onClick={onClose} className="rounded-full border border-lavender/60 px-5 py-2 text-sm font-medium text-cocoa transition hover:bg-lavender/30">
              关闭
            </button>
            <Link to="/pricing" className="rounded-full bg-blush px-5 py-2 text-sm font-semibold text-white shadow-soft transition hover:bg-blush-dark">
              升级永久会员
            </Link>
          </div>
        </div>
      </div>,
      document.body
    )
  }

  const seqTotal = sortedLegend.length

  // portal 到 body：避免祖先 transform 导致 fixed 定位失效
  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col bg-[#faf8f5] text-cocoa">
      {/* 顶栏 */}
      <div className="flex flex-wrap items-center gap-2 border-b border-black/10 px-4 py-2.5">
        <span className="text-sm font-bold">🧩 拼豆模式</span>
        <span className="rounded-md bg-black/5 px-2 py-0.5 text-xs text-cocoa-light">
          {pattern.width}×{pattern.height} 格 · {sortedLegend.length} 色
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <button
            onClick={() => setMirrored((v) => !v)}
            className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${mirrored ? 'border-blush bg-blush text-white' : 'border-lavender/40 bg-white text-cocoa hover:bg-lavender/20'}`}
          >
            镜像 {mirrored ? '开' : '关'}
          </button>
          <button
            onClick={handleConnectBoard}
            className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${board?.connected ? 'border-mint bg-mint/40 text-cocoa' : 'border-lavender/40 bg-white text-cocoa hover:bg-lavender/20'}`}
          >
            {board?.connected ? '🔗 已连接拼豆板' : '🔗 连接拼豆板'}
          </button>
          <button onClick={onClose} className="rounded-lg border border-lavender/40 bg-white px-3 py-1.5 text-xs font-semibold text-cocoa transition hover:bg-lavender/20">
            退出 ✕
          </button>
        </div>
      </div>
      {boardStatus && (
        <p className="border-b border-black/10 bg-cream-100 px-4 py-1.5 text-xs text-cocoa-light">{boardStatus}</p>
      )}

      {/* 图纸区 */}
      <div
        ref={containerRef}
        className="relative flex flex-1 items-center justify-center overflow-hidden"
        style={{ touchAction: 'none' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div
          style={{ transform: `translate(${offset.x}px, ${offset.y}px)`, cursor: dragRef.current ? 'grabbing' : 'grab' }}
        >
          <canvas ref={canvasRef} className="m-2 rounded-lg" />
        </div>
        {/* 缩放控件 */}
        <div className="absolute bottom-3 right-3 flex gap-1.5">
          <button onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))} className="h-9 w-9 rounded-lg border border-lavender/40 bg-white/90 text-cocoa shadow-sm backdrop-blur">−</button>
          <button onClick={() => setZoom(1)} className="h-9 rounded-lg border border-lavender/40 bg-white/90 px-2 text-xs text-cocoa shadow-sm backdrop-blur">复位</button>
          <button onClick={() => setZoom((z) => Math.min(4, z + 0.25))} className="h-9 w-9 rounded-lg border border-lavender/40 bg-white/90 text-cocoa shadow-sm backdrop-blur">＋</button>
        </div>
        {/* 当前色号信息条 */}
        {activeColor && (
          <div className="absolute left-3 top-3 flex items-center gap-2 rounded-xl bg-white/90 px-3 py-2 shadow-soft backdrop-blur">
            <span className="inline-block h-5 w-5 rounded border border-black/20" style={{ backgroundColor: activeColor.hex }} />
            <span className="text-sm font-bold">{activeColor.name}</span>
            <span className="text-xs text-cocoa-light">×{activeCount} 颗</span>
            {mode === 'sequence' && <span className="text-xs text-cocoa-light">{seqIndex + 1} / {seqTotal}</span>}
            <button
              onClick={toggleDone}
              className={`ml-1 rounded-lg border px-2 py-1 text-xs font-semibold transition ${doneSet.has(activeColorId) ? 'border-mint bg-mint/40 text-cocoa' : 'border-lavender/40 bg-white text-cocoa'}`}
            >
              {doneSet.has(activeColorId) ? '✓ 已完成' : '标记完成'}
            </button>
          </div>
        )}
      </div>

      {/* 底部色号面板 */}
      <div className="border-t border-black/10 bg-white px-3 py-2.5">
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          <button
            onClick={() => setMode('all')}
            className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${mode === 'all' ? 'border-blush bg-blush text-white' : 'border-white/20 bg-white/5 text-white/80'}`}
          >
            全部显示
          </button>
          <button
            onClick={() => { setMode('single'); if (!selectedColorId) setSelectedColorId(sortedLegend[0]?.id || null) }}
            className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${mode === 'single' ? 'border-blush bg-blush text-white' : 'border-white/20 bg-white/5 text-white/80'}`}
          >
            单色号
          </button>
          <button
            onClick={() => setMode('sequence')}
            className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${mode === 'sequence' ? 'border-blush bg-blush text-white' : 'border-white/20 bg-white/5 text-white/80'}`}
          >
            依次显示
          </button>
          {mode === 'sequence' && (
            <div className="ml-1 flex items-center gap-1.5">
              <button
                onClick={() => setSeqIndex((i) => Math.max(0, i - 1))}
                disabled={seqIndex === 0}
                className="h-8 w-8 rounded-lg border border-lavender/40 bg-white text-cocoa disabled:opacity-30"
              >◀</button>
              <button
                onClick={() => setSeqIndex((i) => Math.min(seqTotal - 1, i + 1))}
                disabled={seqIndex >= seqTotal - 1}
                className="h-8 w-8 rounded-lg border border-lavender/40 bg-white text-cocoa disabled:opacity-30"
              >▶</button>
            </div>
          )}
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {sortedLegend.map((item, idx) => (
            <button
              key={item.id}
              onClick={() => {
                if (mode === 'sequence') setSeqIndex(idx)
                else { setMode('single'); setSelectedColorId(item.id) }
              }}
              className={`flex shrink-0 flex-col items-center gap-0.5 rounded-lg border px-2 py-1.5 transition ${
                item.id === activeColorId && mode !== 'all'
                  ? 'border-blush bg-blush/20'
                  : 'border-black/10 bg-white hover:bg-cream-100'
              }`}
            >
              <span className="relative inline-block h-6 w-6 rounded border border-black/20" style={{ backgroundColor: item.hex }}>
                {doneSet.has(item.id) && (
                  <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white drop-shadow">✓</span>
                )}
              </span>
              <span className="text-[10px] font-semibold text-cocoa">{item.name}</span>
              <span className="text-[9px] text-cocoa-light">×{item.count}</span>
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body
  )
}
